import { ErrorReporterOriginatedError } from "./ErrorReporterOriginatedError";
export interface ErrorReporter {
  // Manually report an error to GCP error reporting.
  report: (error: Error) => void;
}

export interface ErrorReporterConfig {
  key: string;
  projectId: string;
  service: string;
  version: string;
  userId: string;
  // A number between 0 - 1 that indicates the rate the reporter will log
  // implicitly detected errors. By default a value of 0.1 will be used, which
  // will report 10% of errors it detects.
  sampleRate?: number;
}

// Any regex here will squelch matching error messages and not report them.
const SUPPRESSED_ERROR_REGEXES = [
  // A user trying to edit a notebook without access is not noteworthy to log.
  /Error: User doesn't have Write access to the kernel\./,
  /Error: Permission denied/,
  // Competition acceptance error is not exceptional.
  /To see the data you need to agree to the competition rules/,
  // Do not report 429 / 404 errors.
  /\[ResponseMiddlewares.enforceOk\] response was not ok \| (429|404)/,
  // Any useful data for this error is already lost at this point.
  // We should work on fixing this issue in Roslyn or at the middle tier.
  /Unexpected token e in JSON at position [0-9]+/,
  // stackdriver-errors-js throws some internal errors that we should not log.
  // 4xxs have a chance to loop (429 -> error -> report -> 429 -> ...)
  /Error: 4[0-9]+ http response on stackdriver report.*/,
  // Poor network conditions will not be resolved immediately.
  /Error: network error on stackdriver report.*/,
  // Cross-origin errors won't have stacktrace information available.
  /Uncaught Error: A cross-origin error was thrown.*/,
];

// Default sampling rate for the error reporter is to report 10% of errors
// observed. FE errors are far larger in volume than backend errors, since some
// errors are occuring per page load. Without sampling aggressively, we end up
// creating a lot of noise on the top-level dashboard, and use up reporting
// quota too quickly, which can prevent backend errors from logging.
const DEFAULT_SAMPLE_RATE = 0.1;

// See http://cloud/error-reporting/reference/rest for REST API details, where
// these interfaces and API URLs come from.

// https://cloud.google.com/error-reporting/reference/rest/v1beta1/projects.events/report#reportederrorevent
interface ReportedErrorEvent {
  context: ErrorContext;
  message: string; // If set to error.stack, context.reportLocation is optional
  serviceContext: ServiceContext;
  eventTime?: string; // Filled automatically using server time if not set
}

// https://cloud.google.com/error-reporting/reference/rest/v1beta1/ErrorContext
interface ErrorContext {
  httpRequest: HttpRequestContext;
  user: string;
  reportLocation?: SourceLocation;
  sourceReferences?: SourceReference[];
}

// http://cloud/error-reporting/reference/rest/v1beta1/ServiceContext
interface ServiceContext {
  service: string;
  version: string;
  // resourceType must not be set when reporting errors.
}

// https://cloud.google.com/error-reporting/reference/rest/v1beta1/ErrorContext#httprequestcontext
interface HttpRequestContext {
  // The URL of the request.
  url: string;
  // The user agent information that is provided with the request.
  userAgent: string;
  // The type of HTTP request, such as GET, POST, etc.
  method?: string;
  // The referrer information that is provided with the request.
  referrer?: string;
  // The IP address from which the request originated. This can be IPv4, IPv6,
  // or a token which is derived from the IP address, depending on the data
  // that has been provided in the error report.
  remoteIp?: string;
  // The HTTP response status code for the request.
  responseStatusCode?: number;
}

// http://cloud/error-reporting/reference/rest/v1beta1/ErrorContext#SourceLocation
interface SourceLocation {
  filePath: string;
  // 1-based. 0 indicates that the line number is unknown.
  lineNumber: number;
  functionName: string;
}

// https://cloud.google.com/error-reporting/reference/rest/v1beta1/ErrorContext#sourcereference
interface SourceReference {
  repository: string;
  revisionId: string;
}

/**
 * A utility for logging errors to GCP Error Reporting (formerly Stackdriver).
 *
 * If you want an error logged, its recommended to send it manually via the
 * 'report' method on this class directly. There's support to implicitly log
 * unhandled errors and rejected promises, but that occurs at a sampled rate.
 *
 * Implicitly logged errors are sampled to be kinder to infra, as GCP API quota
 * is shared across a GCP project, including server-side error reporting.
 *
 * FE errors can be viewed at go/kaggle-fe-errors.
 */
export class GcpErrorReporter implements ErrorReporter {
  readonly reportUrl: string;
  readonly serviceContext: ServiceContext;
  readonly userId: string;
  readonly sampleRate: number;

  constructor(config: ErrorReporterConfig) {
    const {
      key,
      projectId,
      service,
      version,
      userId,
      sampleRate = DEFAULT_SAMPLE_RATE,
    } = config;
    this.reportUrl =
      "https://clouderrorreporting.googleapis.com/v1beta1/projects/" +
      `${projectId}/events:report?key=${key}`;
    this.serviceContext = {
      service,
      version,
    };
    this.userId = userId;
    this.sampleRate = sampleRate;
  }

  /**
   * Reports a JS error to GCP error reporting backends. Calling this directly
   * will always send an error report regardless of filtering or error sample
   * rate.
   */
  report(error: Error): Promise<void> {
    const event = this.getReportedErrorEvent(error);
    // Sometimes stacktraces aren't available, such as cross-domain errors.
    if (!event.message) {
      return Promise.resolve();
    }
    return this.sendReportedErrorEvent(event);
  }

  /** Patches console.error to report error messages. */
  startReportingConsoleErrorLogging() {
    const modifiedConsole = (baseConsole => {
      return {
        ...baseConsole,
        error: (...args: any[]) => {
          this.filteredReport(new Error(args.join(" ")));
          baseConsole.error(...args);
        },
      };
    })(window.console);
    window.console = modifiedConsole;
  }

  startReportingUncaughtErrors() {
    const existingHandler = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      // We need to check for an error object explicitly, because it can be
      // missing in some cases (ex: cross-origin errors).
      if (error) {
        this.filteredReport(error);
      }
      return existingHandler?.(message, source, lineno, colno, error) ?? false;
    };
  }

  startReportingUnhandledPromiseRejections() {
    // onunhandledrejection's type definition includes its context as part of
    // the function signature, which isn't correct and confuses the compiler.
    const existingHandler: any = window.onunhandledrejection;
    window.onunhandledrejection = (evt: PromiseRejectionEvent) => {
      // If rejection events were not errors, we ignore them since we don't
      // have enough information to log them (stacktrace, etc).
      if (evt.reason instanceof Error) {
        this.filteredReport(evt.reason);
      }
      existingHandler?.(evt);
    };
  }

  /**
   * Similar to report, but applies filtering to reduce noise and respects
   * sample rate. Visible for testing, generally you want to call report if
   * manually reporting errors.
   */
  filteredReport(error: Error): Promise<void> {
    const shouldLogError = Math.random() < this.sampleRate;
    if (!shouldLogError) {
      return Promise.resolve();
    }

    const message = error.message;
    // Ignore all matching squelched messages.
    if (SUPPRESSED_ERROR_REGEXES.find(regex => regex.test(message))) {
      return Promise.resolve();
    }

    // Ignore bots.
    if (
      !window.navigator ||
      !window.navigator.userAgent ||
      window.navigator.userAgent.toLowerCase().includes("bot")
    ) {
      return Promise.resolve();
    }

    return this.report(error);
  }

  private getReportedErrorEvent(error: Error): ReportedErrorEvent {
    return {
      context: {
        user: this.userId,
        httpRequest: {
          userAgent: window.navigator.userAgent,
          url: window.location.href,
        },
      },
      // This is well supported and should be present on all modern browsers:
      // http://go/mdn/JavaScript/Reference/Global_Objects/Error/Stack
      // If for some reason this isn't set properly, the request will HTTP 400.
      message: error.stack ?? "Error stacktrace was unavailable",
      serviceContext: this.serviceContext,
    };
  }

  private sendReportedErrorEvent(event: ReportedErrorEvent) {
    return fetch(this.reportUrl, {
      method: "post",
      body: JSON.stringify(event),
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
      },
    })
      .then(response => {
        response.text().then(responseText => {
          const succesfulStatus =
            response.status >= 200 && response.status < 300;
          if (!succesfulStatus) {
            const errorMessage =
              "An application error occurred when sending the error report. " +
              `HTTP status: ${response.status}, response: ${responseText}, ` +
              `report message: ${event.message}`;
            throw new ErrorReporterOriginatedError(errorMessage);
          }
        });
      })
      .catch((error: Error) => {
        const errorMessage = `An error occurred when sending the error report: ${error.message}`;
        throw new ErrorReporterOriginatedError(errorMessage);
      });
  }
}
